// Lectures nécessaires au contrôle, par la voie habituelle du backoffice vers
// care : PostgREST derrière le proxy `/api`, cookie de session (comme
// saga/getAffectationsFromLeave.js). Aucune écriture ici.

import { days } from '../../../helpers/date';
import { normalizeDate } from './dates';
import { OVERLAP_TYPES } from './preflight';

export const getJson = async (url) => {
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText} sur ${url}`);
  }
  return response.json();
};

const q = (value) => {
  return encodeURIComponent(value);
};

const overlapFilter = (start, end) => {
  return `start_date=lte.${q(end)}&or=${q(
    `(end_date.is.null,end_date.gte.${start})`,
  )}`;
};

const DATE_FIELDS = ['start_date', 'end_date', 'leave_id'];

// Création : toujours. Allongement : seulement si les dates ou le congé
// changent (décision PO du 19/10 : une édition d'horaires n'est pas contrôlée).
export const shouldCheck = ({ create, values, record }) => {
  if (!values || values.type !== 'Remplacement') {
    return false;
  }
  if (!values.leave_id || !values.start_date || !values.end_date) {
    return false;
  }
  if (create) {
    return true;
  }
  return DATE_FIELDS.some((field) => {
    const before = record ? record[field] : undefined;
    if (field === 'leave_id') {
      return Number(before) !== Number(values[field]);
    }
    return normalizeDate(before) !== normalizeDate(values[field]);
  });
};

// Jours cochés : cases du formulaire d'édition, ou créneaux du planning du
// formulaire de création.
export const weekdaysOf = ({ create, values }) => {
  if (create) {
    const planning = values.planning || {};
    return days.filter((day) => {
      const slots = planning[day];
      const list = Array.isArray(slots) ? slots : [slots];
      return list.some((slot) => {
        return slot && slot.start_time;
      });
    });
  }
  return days.filter((day) => {
    return values[day] === true;
  });
};

// Créneaux du remplacement : un seul en édition (cases des jours et
// horaires) ; en création, un par horaire distinct du planning, comme les
// « buckets » de care (bundle.js getBucketsForDays).
export const slotsOf = ({ create, values }) => {
  if (!create) {
    return [
      {
        weekdays: weekdaysOf({ create, values }),
        start_time: values.start_time,
        end_time: values.end_time,
      },
    ];
  }
  const buckets = {};
  const planning = values.planning || {};
  days.forEach((day) => {
    const slots = Array.isArray(planning[day])
      ? planning[day]
      : [planning[day]];
    slots.forEach((slot) => {
      if (!slot || !slot.start_time) {
        return;
      }
      const key = `${slot.start_time}-${slot.end_time}`;
      if (!buckets[key]) {
        buckets[key] = {
          weekdays: [],
          start_time: slot.start_time,
          end_time: slot.end_time,
        };
      }
      buckets[key].weekdays.push(day);
    });
  });
  return Object.values(buckets);
};

export const cleanerIdOf = (values) => {
  return (
    values.cleaner_id || (values.cleaner_ids && values.cleaner_ids[0]) || null
  );
};

export const fetchSplitContext = async (
  { create, values, record },
  fetchJson = getJson,
) => {
  const leaveId = Number(values.leave_id);
  const start = normalizeDate(values.start_date);
  const end = normalizeDate(values.end_date);

  const [contracts, leaveRows, leaveInfos] = await Promise.all([
    fetchJson(
      `/api/contract?id=eq.${q(
        values.contract_id,
      )}&select=id,account_id,account_name,effective_date,end_date`,
    ),
    fetchJson(
      `/api/leave?id=eq.${leaveId}&select=id,cleaner_id,contract_id,start_date,end_date`,
    ),
    fetchJson(`/api/leave_info?id=eq.${leaveId}&select=id,fullname`),
  ]);
  const contract = contracts[0];
  const leave1 = leaveRows[0];
  if (!contract || !leave1 || !leave1.cleaner_id) {
    return null;
  }
  const accountId =
    (!create && record && record.contract_id === values.contract_id
      ? record.account_id
      : null) || contract.account_id;
  const accountName =
    (!create && record && record.contract_id === values.contract_id
      ? record.account_name
      : null) || contract.account_name;
  const absentId = leave1.cleaner_id;

  const [leaves, absences, postes, leave1Absences] = await Promise.all([
    fetchJson(
      `/api/leave?cleaner_id=eq.${q(absentId)}&${overlapFilter(
        start,
        end,
      )}&select=id,start_date,end_date,contract_id&order=start_date.asc`,
    ),
    fetchJson(
      `/api/affectation?type=eq.Absence&cleaner_id=eq.${q(
        absentId,
      )}&account_id=eq.${q(accountId)}&leave_id=not.is.null&${overlapFilter(
        start,
        end,
      )}&select=id,leave_id,start_date,end_date`,
    ),
    fetchJson(
      `/api/affectation?type=not.in.(Absence,Remplacement)&cleaner_id=eq.${q(
        absentId,
      )}&account_id=eq.${q(accountId)}&${overlapFilter(
        start,
        end,
      )}&select=id,type,start_date,end_date`,
    ),
    // Toutes les absences du congé lié sur le site, quelles que soient leurs
    // dates : la phrase de tête dit si elles finissent avant R ou commencent
    // après lui.
    fetchJson(
      `/api/affectation?type=eq.Absence&leave_id=eq.${leaveId}&account_id=eq.${q(
        accountId,
      )}&select=id,leave_id,start_date,end_date`,
    ),
  ]);

  const absentName = (leaveInfos[0] && leaveInfos[0].fullname) || "l'absent(e)";
  const replacement = {
    id: create ? undefined : record && record.id,
    start_date: start,
    end_date: end,
    weekdays: weekdaysOf({ create, values }),
    leave_id: leaveId,
    contract_id: values.contract_id,
    account_name: accountName,
  };
  return {
    contract,
    replacement,
    slots: slotsOf({ create, values }),
    input: {
      replacement,
      absentName,
      leaves: [leave1].concat(
        leaves.filter((leave) => {
          return Number(leave.id) !== leaveId;
        }),
      ),
      absences,
      postes,
      leave1Absences,
    },
  };
};

// Données du pré-contrôle : contrat du remplaçant et ses autres affectations
// sur la période du remplacement tel que saisi (hors le remplacement
// lui-même en édition). Lu une fois, à l'ouverture du contrôle : les morceaux
// sont tous compris dans cette période.
export const fetchPreflightData = async (
  { cleanerId, replacementId, start, end },
  fetchJson = getJson,
) => {
  const types = OVERLAP_TYPES.map((type) => {
    return `"${type}"`;
  }).join(',');
  const exclude = replacementId ? `&id=neq.${q(replacementId)}` : '';
  const [cleaners, others] = await Promise.all([
    fetchJson(
      `/api/cleaner?id=eq.${q(
        cleanerId,
      )}&select=id,fullname,contract_start_date,contract_end_date`,
    ),
    fetchJson(
      `/api/affectation?cleaner_id=eq.${q(cleanerId)}${exclude}&type=in.${q(
        `(${types})`,
      )}&${overlapFilter(
        start,
        end,
      )}&select=id,type,account_name,start_date,end_date,start_time,end_time,${days.join(
        ',',
      )}`,
    ),
  ]);
  return { cleaner: cleaners[0], others };
};
